import React, { useRef, useEffect, useCallback } from 'react';
import { Grid, Element, Agent, ElementType } from '../types';
import { GRID_WIDTH, GRID_HEIGHT } from '../constants';

interface SimulationCanvasProps {
  grid: Grid;
  elements: Element[];
  agents: Agent[];
  selectedElementId: string;
  onDraw: (x: number, y: number) => void;
  onStartDraw: (x: number, y: number) => void;
  onStopDraw: () => void;
  onMoveDraw: (x: number, y: number) => void;
  onCastTornado: (start: { x: number; y: number }, end: { x: number; y: number }) => void;
  onCastLightning: (start: { x: number; y: number }, end: { x: number; y: number }) => void;
  onCastMeteor: (pos: { x: number; y: number }) => void;
  onCastBlackHole: (start: { x: number; y: number }, end: { x: number; y: number }) => void;
  onCastRainCloud: (pos: { x: number; y: number }) => void;
  onCastBomb: (pos: { x: number; y: number }) => void;
  onCastFreezeRay: (start: { x: number; y: number }, end: { x: number; y: number }) => void;
  onCastEarthquake: (pos: { x: number; y: number }) => void;
  onCastTsunami: (pos: { x: number; y: number }) => void;
  brushSize: number;
  cellSize: number;
  glowEffects?: boolean;
}

const SimulationCanvas: React.FC<SimulationCanvasProps> = ({
  grid,
  elements,
  agents,
  selectedElementId,
  onDraw,
  onStartDraw,
  onStopDraw,
  onMoveDraw,
  onCastTornado,
  onCastLightning,
  onCastMeteor,
  onCastBlackHole,
  onCastRainCloud,
  onCastBomb,
  onCastFreezeRay,
  onCastEarthquake,
  onCastTsunami,
  brushSize,
  cellSize,
  glowEffects = true,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const isDrawingRef = useRef(false);
  const dragStartRef = useRef<{ x: number; y: number } | null>(null);
  const elementMap = React.useMemo(() => new Map(elements.map(el => [el.id, el])), [elements]);

  const getGridPos = useCallback((e: React.MouseEvent | React.TouchEvent) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    const clientX = 'touches' in e ? e.touches[0].clientX : (e as React.MouseEvent).clientX;
    const clientY = 'touches' in e ? e.touches[0].clientY : (e as React.MouseEvent).clientY;
    const x = Math.floor((clientX - rect.left) / cellSize);
    const y = Math.floor((clientY - rect.top) / cellSize);
    return { x, y };
  }, [cellSize]);

  const handleMouseDown = (e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    const pos = getGridPos(e);
    
    if (selectedElementId === 'TORNADO' || selectedElementId === 'LIGHTNING' || 
        selectedElementId === 'BLACKHOLE' || selectedElementId === 'FREEZE_RAY') {
      dragStartRef.current = pos;
    } else if (selectedElementId === 'METEOR' || selectedElementId === 'RAINCLOUD' || 
               selectedElementId === 'BOMB' || selectedElementId === 'EARTHQUAKE' || 
               selectedElementId === 'TSUNAMI') {
      if (selectedElementId === 'METEOR') onCastMeteor(pos);
      else if (selectedElementId === 'RAINCLOUD') onCastRainCloud(pos);
      else if (selectedElementId === 'BOMB') onCastBomb(pos);
      else if (selectedElementId === 'EARTHQUAKE') onCastEarthquake(pos);
      else if (selectedElementId === 'TSUNAMI') onCastTsunami(pos);
    } else {
      isDrawingRef.current = true;
      onStartDraw(pos.x, pos.y);
    }
  };

  const handleMouseMove = (e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    const pos = getGridPos(e);
    if (isDrawingRef.current) {
      onMoveDraw(pos.x, pos.y);
    }
  };

  const handleMouseUp = (e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    const pos = getGridPos(e);
    
    if (dragStartRef.current) {
      const start = dragStartRef.current;
      if (selectedElementId === 'TORNADO') onCastTornado(start, pos);
      else if (selectedElementId === 'LIGHTNING') onCastLightning(start, pos);
      else if (selectedElementId === 'BLACKHOLE') onCastBlackHole(start, pos);
      else if (selectedElementId === 'FREEZE_RAY') onCastFreezeRay(start, pos);
      dragStartRef.current = null;
    } else if (isDrawingRef.current) {
      isDrawingRef.current = false;
      onStopDraw();
    }
  };

  const handleMouseLeave = () => {
    if (isDrawingRef.current) {
      isDrawingRef.current = false;
      onStopDraw();
    }
    dragStartRef.current = null;
  };

  // Drawing function
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Clear canvas
    ctx.fillStyle = '#111827';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Draw grid cells
    for (let y = 0; y < GRID_HEIGHT; y++) {
      for (let x = 0; x < GRID_WIDTH; x++) {
        const particle = grid[y][x];
        if (particle.elementId !== ElementType.EMPTY) {
          const element = elementMap.get(particle.elementId);
          if (element) {
            let color = element.color;
            
            // Apply temperature-based color variations
            const temp = particle.temperature ?? 20;
            if (temp > 500) {
              // Hot - add red/orange tint
              const intensity = Math.min(1, (temp - 500) / 1500);
              ctx.fillStyle = blendColors(color, '#ff4400', intensity * 0.3);
            } else if (temp < 0) {
              // Cold - add blue tint
              const intensity = Math.min(1, Math.abs(temp) / 50);
              ctx.fillStyle = blendColors(color, '#00aaff', intensity * 0.3);
            } else {
              ctx.fillStyle = color;
            }

            // Apply glow effects for hot elements
            if (glowEffects && (particle.elementId === ElementType.FIRE || 
                particle.elementId === ElementType.LAVA || 
                particle.elementId === ElementType.MAGMA ||
                particle.elementId === ElementType.EMBER)) {
              ctx.shadowColor = color;
              ctx.shadowBlur = 8;
            } else if (glowEffects && particle.elementId === ElementType.ELECTRIC_SPARK) {
              ctx.shadowColor = '#ffff00';
              ctx.shadowBlur = 12;
            } else if (glowEffects && (particle.elementId === ElementType.ICE || particle.elementId === ElementType.FREEZE_RAY)) {
              ctx.shadowColor = '#a5f3fc';
              ctx.shadowBlur = 4;
            } else {
              ctx.shadowBlur = 0;
            }

            ctx.fillRect(x * cellSize, y * cellSize, cellSize, cellSize);
            ctx.shadowBlur = 0;
          }
        }
      }
    }

    // Draw agents (spells)
    for (const agent of agents) {
      drawAgent(ctx, agent, cellSize);
    }

    // Draw drag preview for spells
    if (dragStartRef.current) {
      drawDragPreview(ctx, dragStartRef.current, selectedElementId, cellSize);
    }
  }, [grid, agents, elementMap, cellSize, glowEffects, selectedElementId]);

  const drawAgent = (ctx: CanvasRenderingContext2D, agent: Agent, cellSize: number) => {
    switch (agent.type) {
      case 'TORNADO': {
        const tornado = agent as { x: number; y: number; radius: number; lifetime: number };
        const alpha = Math.min(1, tornado.lifetime / 20);
        const gradient = ctx.createRadialGradient(
          tornado.x * cellSize, tornado.y * cellSize, 0,
          tornado.x * cellSize, tornado.y * cellSize, tornado.radius * cellSize
        );
        gradient.addColorStop(0, `rgba(50, 50, 50, ${alpha * 0.8})`);
        gradient.addColorStop(0.5, `rgba(100, 100, 100, ${alpha * 0.4})`);
        gradient.addColorStop(1, 'rgba(150, 150, 150, 0)');
        ctx.fillStyle = gradient;
        ctx.beginPath();
        ctx.arc(tornado.x * cellSize, tornado.y * cellSize, tornado.radius * cellSize, 0, Math.PI * 2);
        ctx.fill();
        break;
      }

      case 'LIGHTNING': {
        const lightning = agent as { path: { x: number; y: number }[]; lifetime: number };
        const alpha = Math.min(1, lightning.lifetime / 5);
        ctx.strokeStyle = `rgba(255, 255, 100, ${alpha})`;
        ctx.lineWidth = 3;
        ctx.shadowColor = '#ffff00';
        ctx.shadowBlur = 15;
        ctx.beginPath();
        if (lightning.path.length > 0) {
          ctx.moveTo(lightning.path[0].x * cellSize, lightning.path[0].y * cellSize);
          for (let i = 1; i < lightning.path.length; i++) {
            ctx.lineTo(lightning.path[i].x * cellSize, lightning.path[i].y * cellSize);
          }
        }
        ctx.stroke();
        ctx.shadowBlur = 0;
        break;
      }

      case 'METEOR': {
        const meteor = agent as { x: number; y: number; size: number };
        const gradient = ctx.createRadialGradient(
          meteor.x * cellSize, meteor.y * cellSize, 0,
          meteor.x * cellSize, meteor.y * cellSize, meteor.size * cellSize * 2
        );
        gradient.addColorStop(0, '#ffaa00');
        gradient.addColorStop(0.3, '#ff4400');
        gradient.addColorStop(0.6, '#ff0000');
        gradient.addColorStop(1, 'rgba(100, 0, 0, 0)');
        ctx.fillStyle = gradient;
        ctx.shadowColor = '#ff4400';
        ctx.shadowBlur = 20;
        ctx.beginPath();
        ctx.arc(meteor.x * cellSize, meteor.y * cellSize, meteor.size * cellSize * 2, 0, Math.PI * 2);
        ctx.fill();
        ctx.shadowBlur = 0;
        break;
      }

      case 'BLACKHOLE': {
        const bh = agent as { x: number; y: number; radius: number; lifetime: number };
        const alpha = Math.min(1, bh.lifetime / 20);
        
        // Accretion disk
        const diskGradient = ctx.createRadialGradient(
          bh.x * cellSize, bh.y * cellSize, bh.radius * cellSize * 0.3,
          bh.x * cellSize, bh.y * cellSize, bh.radius * cellSize
        );
        diskGradient.addColorStop(0, 'rgba(0, 0, 0, 0)');
        diskGradient.addColorStop(0.5, `rgba(100, 0, 150, ${alpha * 0.5})`);
        diskGradient.addColorStop(0.8, `rgba(50, 0, 100, ${alpha * 0.3})`);
        diskGradient.addColorStop(1, 'rgba(0, 0, 0, 0)');
        ctx.fillStyle = diskGradient;
        ctx.beginPath();
        ctx.arc(bh.x * cellSize, bh.y * cellSize, bh.radius * cellSize, 0, Math.PI * 2);
        ctx.fill();

        // Event horizon
        ctx.fillStyle = `rgba(0, 0, 0, ${alpha})`;
        ctx.beginPath();
        ctx.arc(bh.x * cellSize, bh.y * cellSize, bh.radius * cellSize * 0.3, 0, Math.PI * 2);
        ctx.fill();
        break;
      }

      case 'RAINCLOUD': {
        const cloud = agent as { x: number; y: number; width: number };
        const gradient = ctx.createRadialGradient(
          cloud.x * cellSize, cloud.y * cellSize, 0,
          cloud.x * cellSize, cloud.y * cellSize, cloud.width * cellSize
        );
        gradient.addColorStop(0, 'rgba(100, 100, 120, 0.6)');
        gradient.addColorStop(0.5, 'rgba(80, 80, 100, 0.4)');
        gradient.addColorStop(1, 'rgba(60, 60, 80, 0)');
        ctx.fillStyle = gradient;
        ctx.beginPath();
        ctx.arc(cloud.x * cellSize, cloud.y * cellSize, cloud.width * cellSize, 0, Math.PI * 2);
        ctx.fill();
        break;
      }

      case 'EXPLOSION': {
        const explosion = agent as { x: number; y: number; radius: number; maxRadius: number; lifetime: number };
        const progress = explosion.radius / explosion.maxRadius;
        const alpha = Math.max(0, 1 - progress) * Math.min(1, explosion.lifetime / 10);
        
        const gradient = ctx.createRadialGradient(
          explosion.x * cellSize, explosion.y * cellSize, 0,
          explosion.x * cellSize, explosion.y * cellSize, explosion.radius * cellSize
        );
        gradient.addColorStop(0, `rgba(255, 255, 200, ${alpha})`);
        gradient.addColorStop(0.3, `rgba(255, 150, 50, ${alpha * 0.8})`);
        gradient.addColorStop(0.6, `rgba(255, 50, 0, ${alpha * 0.5})`);
        gradient.addColorStop(1, 'rgba(100, 0, 0, 0)');
        ctx.fillStyle = gradient;
        ctx.beginPath();
        ctx.arc(explosion.x * cellSize, explosion.y * cellSize, explosion.radius * cellSize, 0, Math.PI * 2);
        ctx.fill();
        break;
      }

      case 'BOMB_AGENT': {
        const bomb = agent as { x: number; y: number; timer: number; maxTimer: number };
        const blink = Math.floor(Date.now() / 200) % 2 === 0;
        ctx.fillStyle = blink ? '#ff0000' : '#aa0000';
        ctx.fillRect(bomb.x * cellSize - 2, bomb.y * cellSize - 2, 4, 4);
        break;
      }

      case 'FREEZE_RAY': {
        const freezeRay = agent as { path: { x: number; y: number }[]; lifetime: number };
        const alpha = Math.min(1, freezeRay.lifetime / 5);
        ctx.strokeStyle = `rgba(100, 200, 255, ${alpha})`;
        ctx.lineWidth = 4;
        ctx.shadowColor = '#00ffff';
        ctx.shadowBlur = 10;
        ctx.beginPath();
        if (freezeRay.path.length > 0) {
          ctx.moveTo(freezeRay.path[0].x * cellSize, freezeRay.path[0].y * cellSize);
          for (let i = 1; i < freezeRay.path.length; i++) {
            ctx.lineTo(freezeRay.path[i].x * cellSize, freezeRay.path[i].y * cellSize);
          }
        }
        ctx.stroke();
        ctx.shadowBlur = 0;
        break;
      }

      case 'EARTHQUAKE': {
        const earthquake = agent as { x: number; y: number; radius: number; maxRadius: number };
        const progress = earthquake.radius / earthquake.maxRadius;
        const alpha = Math.max(0, 1 - progress);
        
        ctx.strokeStyle = `rgba(139, 69, 19, ${alpha})`;
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.arc(earthquake.x * cellSize, earthquake.y * cellSize, earthquake.radius * cellSize, 0, Math.PI * 2);
        ctx.stroke();
        break;
      }

      case 'TSUNAMI': {
        const tsunami = agent as { x: number; y: number; width: number; height: number };
        const gradient = ctx.createLinearGradient(
          tsunami.x * cellSize, 0,
          (tsunami.x + 10) * cellSize, 0
        );
        gradient.addColorStop(0, 'rgba(0, 100, 200, 0.8)');
        gradient.addColorStop(0.5, 'rgba(0, 150, 255, 0.6)');
        gradient.addColorStop(1, 'rgba(0, 100, 200, 0.4)');
        ctx.fillStyle = gradient;
        ctx.fillRect(tsunami.x * cellSize, tsunami.y * cellSize, tsunami.width * cellSize, tsunami.height * cellSize);
        break;
      }
    }
  };

  const drawDragPreview = (ctx: CanvasRenderingContext2D, start: { x: number; y: number }, elementId: string, cellSize: number) => {
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.5)';
    ctx.setLineDash([5, 5]);
    ctx.lineWidth = 2;
    
    switch (elementId) {
      case 'TORNADO':
        ctx.strokeStyle = 'rgba(167, 243, 208, 0.5)';
        ctx.beginPath();
        ctx.arc(start.x * cellSize, start.y * cellSize, 20 * cellSize, 0, Math.PI * 2);
        ctx.stroke();
        break;
      case 'LIGHTNING':
      case 'FREEZE_RAY':
        ctx.strokeStyle = elementId === 'FREEZE_RAY' ? 'rgba(0, 255, 255, 0.5)' : 'rgba(253, 224, 71, 0.5)';
        ctx.beginPath();
        ctx.moveTo(start.x * cellSize, start.y * cellSize);
        ctx.lineTo(start.x * cellSize + 50, start.y * cellSize);
        ctx.stroke();
        break;
      case 'BLACKHOLE':
        ctx.strokeStyle = 'rgba(59, 7, 100, 0.5)';
        ctx.beginPath();
        ctx.arc(start.x * cellSize, start.y * cellSize, 25 * cellSize, 0, Math.PI * 2);
        ctx.stroke();
        break;
    }
    
    ctx.setLineDash([]);
  };

  // Helper function to blend colors
  const blendColors = (color1: string, color2: string, ratio: number): string => {
    const hex2rgb = (hex: string): [number, number, number] => {
      const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
      return result ? [
        parseInt(result[1], 16),
        parseInt(result[2], 16),
        parseInt(result[3], 16)
      ] : [0, 0, 0];
    };

    const rgb1 = hex2rgb(color1);
    const rgb2 = hex2rgb(color2);

    const r = Math.round(rgb1[0] * (1 - ratio) + rgb2[0] * ratio);
    const g = Math.round(rgb1[1] * (1 - ratio) + rgb2[1] * ratio);
    const b = Math.round(rgb1[2] * (1 - ratio) + rgb2[2] * ratio);

    return `rgb(${r}, ${g}, ${b})`;
  };

  return (
    <canvas
      ref={canvasRef}
      width={GRID_WIDTH * cellSize}
      height={GRID_HEIGHT * cellSize}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseLeave}
      onTouchStart={handleMouseDown}
      onTouchMove={handleMouseMove}
      onTouchEnd={handleMouseUp}
      className="cursor-crosshair touch-none"
      style={{
        imageRendering: 'pixelated',
        boxShadow: '0 0 20px rgba(0, 0, 0, 0.5)',
      }}
    />
  );
};

export default SimulationCanvas;
